import { HexCoord, HexTileData, TileType } from './HexTypes';

/**
 * 六角形タイルクラス
 */
export class HexTile {
  public readonly coords: HexCoord;
  public readonly id: string;
  public readonly ring: number;
  
  private _neighbors: HexTile[] | null = null;
  private _isHighlighted: boolean = false;
  private _isSelected: boolean = false;
  private _tileType: TileType = TileType.EMPTY;
  private _isNewlyPlaced: boolean = false;
  
  constructor(coords: HexCoord, ring: number) {
    this.coords = coords;
    this.id = HexTile.coordToId(coords);
    this.ring = ring;
  }
  
  /**
   * 座標をID文字列に変換
   */
  public static coordToId(coord: HexCoord): string {
    return `${coord.x},${coord.y},${coord.z}`;
  }
  
  /**
   * ID文字列を座標に変換
   */
  public static idToCoord(id: string): HexCoord {
    const [x, y, z] = id.split(',').map(Number);
    return { x, y, z };
  }
  
  /**
   * 隣接タイルを設定（ボードによって設定される）
   */
  public setNeighbors(neighbors: HexTile[]): void {
    this._neighbors = neighbors;
  }
  
  /**
   * 隣接タイルを取得
   */
  public getNeighbors(): HexTile[] {
    return this._neighbors || [];
  }
  
  /**
   * 特定のタイルが隣接しているか判定
   */
  public isNeighbor(tile: HexTile): boolean {
    return this.getNeighbors().includes(tile);
  }
  
  /**
   * ハイライト状態の設定
   */
  public setHighlighted(highlighted: boolean): void {
    this._isHighlighted = highlighted;
  }
  
  public isHighlighted(): boolean {
    return this._isHighlighted;
  }
  
  /**
   * 選択状態の設定
   */
  public setSelected(selected: boolean): void {
    this._isSelected = selected;
  }
  
  public isSelected(): boolean {
    return this._isSelected;
  }
  
  /**
   * タイルタイプの設定
   */
  public setTileType(type: TileType): void {
    this._tileType = type;
  }
  
  public getTileType(): TileType {
    return this._tileType;
  }
  
  /**
   * 新規配置フラグの設定
   */
  public setNewlyPlaced(isNew: boolean): void {
    this._isNewlyPlaced = isNew;
  }
  
  public isNewlyPlaced(): boolean {
    return this._isNewlyPlaced;
  }
  
  /**
   * 星マーク付きタイルかどうか判定
   */
  public hasStar(): boolean {
    return this._tileType.includes('_STAR');
  }
  
  /**
   * 星マークなしの基本タイプを取得
   */
  public getBaseTileType(): TileType {
    if (this.hasStar()) {
      return this._tileType.replace('_STAR', '') as TileType;
    }
    return this._tileType;
  }
  
  /**
   * タイルデータを取得
   */
  public getData(): HexTileData {
    return {
      id: this.id,
      coords: this.coords,
      isHighlighted: this._isHighlighted,
      isSelected: this._isSelected,
      ring: this.ring,
      tileType: this._tileType
    };
  }
  
  /**
   * 2つのタイル間の距離を計算
   */
  public static distance(a: HexTile, b: HexTile): number {
    return (
      Math.abs(a.coords.x - b.coords.x) +
      Math.abs(a.coords.y - b.coords.y) +
      Math.abs(a.coords.z - b.coords.z)
    ) / 2;
  }
  
  /**
   * このタイルから別のタイルへの距離
   */
  public distanceTo(other: HexTile): number {
    return HexTile.distance(this, other);
  }
}